import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, FileSpreadsheet, AlertTriangle, Save, Copy, Check } from 'lucide-react';
import api from '../services/api';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar, { descargarArchivo } from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import { pref } from '../utils/moneda';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Egresos del mes por categoría (pendiente #33): cómo se reparte el 100%
// de las salidas del mes entre los grupos (categoría padre) y cada
// sub-categoría. El backend manda cada monto ya convertido a USD y a BCV,
// así el selector cambia de vista sin volver a pedir datos.
// ============================================================

// "2026-08" -> "agosto 2026"
function nombreMes(mes) {
  const [a, m] = mes.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
}

function formatPct(v) {
  return `${formatMonto(v * 100)}%`;
}

// Barra de peso junto al % — da la lectura de "incidencia" de un vistazo.
function BarraPct({ valor, clase }) {
  return (
    <div className="h-1.5 w-16 shrink-0 bg-slate-100 rounded-full overflow-hidden print:hidden">
      <div className={`h-full rounded-full ${clase}`} style={{ width: `${Math.min(100, valor * 100)}%` }} />
    </div>
  );
}

// Pide la tasa de los días con egresos en Bs que no la tienen cargada:
// sin ella esos egresos no se pueden convertir y el 100% queda corto.
// Guarda con el mismo endpoint de Tasas de cambio y el reporte se recalcula.
function TasasFaltantes({ fechas, onGuardado }) {
  const [valores, setValores] = useState(() => Object.fromEntries(
    fechas.map((f) => [f.fecha, { tasa_bcv: f.tasa_bcv || '', tasa_usd_generica: f.tasa_usd_generica || '' }])
  ));
  const [guardando, setGuardando] = useState(null);

  function cambiar(fecha, campo, valor) {
    setValores((prev) => ({ ...prev, [fecha]: { ...prev[fecha], [campo]: valor } }));
  }

  async function guardar(fecha) {
    const v = valores[fecha];
    if (!(Number(v.tasa_bcv) > 0) || !(Number(v.tasa_usd_generica) > 0)) {
      alert('Ingresá la tasa BCV y la tasa USD del día');
      return;
    }
    setGuardando(fecha);
    try {
      await api.post('/tasas-cambio', { fecha, tasa_bcv: Number(v.tasa_bcv), tasa_usd_generica: Number(v.tasa_usd_generica) });
      window.dispatchEvent(new Event('tasas-cambio:actualizado'));
      onGuardado();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la tasa');
    } finally {
      setGuardando(null);
    }
  }

  return (
    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 space-y-2 print:hidden">
      <p className="flex items-center gap-2 font-medium">
        <AlertTriangle size={16} className="shrink-0" />
        Falta la tasa de {fechas.length === 1 ? '1 día' : `${fechas.length} días`} con egresos en Bs. Cargala para que entren en el total.
      </p>
      {fechas.map((f) => (
        <div key={f.fecha} className="flex items-center gap-2 flex-wrap">
          <span className="w-24 font-medium">{formatFecha(f.fecha)}</span>
          <input
            type="number" step="0.0001" min="0" placeholder="Tasa BCV"
            value={valores[f.fecha]?.tasa_bcv ?? ''}
            onChange={(e) => cambiar(f.fecha, 'tasa_bcv', e.target.value)}
            className="border rounded-md px-3 py-1.5 w-32 bg-white"
          />
          <input
            type="number" step="0.0001" min="0" placeholder="Tasa USD"
            value={valores[f.fecha]?.tasa_usd_generica ?? ''}
            onChange={(e) => cambiar(f.fecha, 'tasa_usd_generica', e.target.value)}
            className="border rounded-md px-3 py-1.5 w-32 bg-white"
          />
          <button
            onClick={() => guardar(f.fecha)}
            disabled={guardando === f.fecha}
            title="Guardar tasa"
            className="text-amber-700 hover:text-primary-600 transition-colors disabled:opacity-50"
          >
            <Save size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

// Detalle de una línea del reporte: los egresos de esa categoría en el mes.
// Muestra el monto original (Bs o $) y el convertido a la vista elegida,
// que suma lo mismo que la fila del reporte.
function DetalleCategoria({ mes, linea, grupoNombre, verEn, onClose }) {
  const [movs, setMovs] = useState(null);
  const [error, setError] = useState('');
  const clave = verEn === 'BCV' ? 'bcv' : 'usd';

  useEffect(() => {
    api.get('/reportes/egresos-categoria/movimientos', { params: { mes, categoria_id: linea.id ?? 'sin' } })
      .then(({ data }) => setMovs(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudieron cargar los movimientos'));
  }, [mes, linea.id]);

  const total = (movs || []).reduce((s, m) => s + (m[clave] || 0), 0);
  const titulo = grupoNombre && grupoNombre !== linea.nombre ? `${grupoNombre} › ${linea.nombre}` : linea.nombre;
  const [copiado, setCopiado] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Copia como texto separado por tabuladores: se pega en Excel/Sheets
  // directo en columnas, con los montos en formato es (coma decimal).
  async function copiar() {
    const filas = [
      ['Fecha', 'Cuenta', 'Descripción', 'Referencia', 'Moneda', 'Monto', `Monto ${verEn}`],
      ...movs.map((m) => [
        formatFecha(m.fecha),
        m.cuenta_nombre,
        (m.descripcion || m.nombre_receptor || '').replace(/\s+/g, ' '),
        m.referencia || '',
        m.cuenta_moneda === 'BS' ? 'Bs' : 'USD',
        formatMonto(m.monto),
        m[clave] != null ? formatMonto(m[clave]) : 'sin tasa'
      ]),
      ['', '', `Total · ${movs.length} salidas`, '', '', '', formatMonto(total)]
    ];
    try {
      await navigator.clipboard.writeText(filas.map((f) => f.join('\t')).join('\n'));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      alert('No se pudo copiar al portapapeles');
    }
  }

  async function descargarExcel() {
    setExportando(true);
    try {
      const { data } = await api.get('/reportes/egresos-categoria/movimientos/excel', {
        params: { mes, categoria_id: linea.id ?? 'sin', vista: verEn },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `movimientos-${linea.nombre.replace(/[^\w\sáéíóúñÁÉÍÓÚÑ-]/g, '').trim()}-${mes}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('No se pudo descargar el Excel');
    } finally {
      setExportando(false);
    }
  }

  return (
    <Modal title={`${titulo} · ${nombreMes(mes)}`} onClose={onClose} maxWidth="max-w-4xl">
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {!movs && !error && <p className="text-slate-400 text-sm">Cargando…</p>}
      {movs && movs.length > 0 && (
        <div className="flex items-center justify-end gap-3 mb-2">
          <button onClick={copiar} title="Copiar movimientos" className="text-slate-400 hover:text-primary-600 transition-colors">
            {copiado ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
          </button>
          <button
            onClick={descargarExcel}
            disabled={exportando}
            title="Descargar en Excel"
            className="text-slate-400 hover:text-green-600 transition-colors disabled:opacity-50"
          >
            <FileSpreadsheet size={16} />
          </button>
        </div>
      )}
      {movs && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-3 py-1.5">Fecha</th>
                <th className="px-3 py-1.5">Cuenta</th>
                <th className="px-3 py-1.5">Descripción</th>
                <th className="px-3 py-1.5 text-right">Monto</th>
                <th className="px-3 py-1.5 text-right">{verEn}</th>
              </tr>
            </thead>
            <tbody>
              {movs.map((m) => (
                <tr key={m.id} className="border-t hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-1.5 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-slate-500">{m.cuenta_nombre}</td>
                  <td className="px-3 py-1.5 break-words">
                    {m.descripcion || m.nombre_receptor || <span className="text-slate-400">—</span>}
                    {m.referencia && <span className="text-xs text-slate-400"> · Ref {m.referencia}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap text-slate-500">
                    {m.cuenta_moneda === 'BS' ? 'Bs ' : '$'}{formatMonto(m.monto)}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap font-medium">
                    {m[clave] != null ? `${pref(verEn)}${formatMonto(m[clave])}` : <span className="text-amber-600" title="Falta la tasa del día">sin tasa</span>}
                  </td>
                </tr>
              ))}
              {movs.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-slate-400">No hay egresos en esta categoría este mes.</td></tr>
              )}
            </tbody>
            {movs.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-bold text-slate-800">
                  <td className="px-3 py-1.5" colSpan={4}>Total · {movs.length} {movs.length === 1 ? 'salida' : 'salidas'}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Modal>
  );
}

export default function ReporteEgresosCategoria() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [mes, setMes] = useState(() => hoyCaracas().slice(0, 7));
  const [verEn, setVerEn] = useVistaMoneda();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [colapsados, setColapsados] = useState(new Set());
  const [detalle, setDetalle] = useState(null); // { linea, grupoNombre }

  function cargar() {
    setCargando(true);
    setError('');
    api.get('/reportes/egresos-categoria', { params: { mes } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el reporte de egresos'))
      .finally(() => setCargando(false));
  }

  useEffect(() => {
    if (empresaActiva && mes) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, mes]);

  const clave = verEn === 'BCV' ? 'bcv' : 'usd';
  const total = datos ? datos.total[clave] : 0;
  const pctTotal = (v) => (total > 0 ? v / total : 0);
  const grupos = datos ? [...datos.grupos].sort((a, b) => b[clave] - a[clave]) : [];

  function alternar(id) {
    setColapsados((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  function alternarTodos() {
    setColapsados((prev) => (prev.size > 0 ? new Set() : new Set(grupos.map((g) => g.id))));
  }

  async function exportarExcel() {
    const { data } = await api.get('/reportes/egresos-categoria/excel', {
      params: { mes, vista: verEn },
      responseType: 'blob'
    });
    descargarArchivo(data, `egresos-por-categoria-${mes}${empresaActivaNombre ? `-${empresaActivaNombre}` : ''}.xlsx`);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Egresos por categoría
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="month"
            value={mes}
            onChange={(e) => e.target.value && setMes(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
          <SelectorMoneda vista={verEn} onChange={setVerEn} />
          <Exportar onExcel={exportarExcel} disabled={!datos || datos.grupos.length === 0} />
        </div>
      </div>

      <EncabezadoImpresion titulo="Egresos por categoría" detalle={`${nombreMes(mes)} · montos en ${verEn}`} />

      {detalle && (
        <DetalleCategoria
          mes={mes}
          linea={detalle.linea}
          grupoNombre={detalle.grupoNombre}
          verEn={verEn}
          onClose={() => setDetalle(null)}
        />
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : datos && (
        <>
          <div className="bg-white rounded-xl shadow-sm px-4 py-2.5 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
            <span>
              <span className="text-slate-500">Total egresos </span>
              <span className="text-lg font-bold text-red-600">{pref(verEn)}{formatMonto(total)}</span>
            </span>
            <span>
              <span className="text-slate-500">Salidas </span>
              <span className="font-semibold text-slate-700">{datos.cantidad}</span>
            </span>
            {grupos[0] && (
              <span className="min-w-0 truncate">
                <span className="text-slate-500">Mayor peso </span>
                <span className="font-semibold text-slate-700">{grupos[0].nombre} · {formatPct(pctTotal(grupos[0][clave]))}</span>
              </span>
            )}
          </div>

          {datos.fechas_sin_tasa.length > 0 && (
            <TasasFaltantes key={datos.fechas_sin_tasa.map((f) => f.fecha).join()} fechas={datos.fechas_sin_tasa} onGuardado={cargar} />
          )}

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-3 py-1.5">
                      <button type="button" onClick={alternarTodos} className="inline-flex items-center gap-1 hover:text-slate-700 print:pointer-events-none">
                        {colapsados.size > 0 ? <ChevronRight size={14} className="print:hidden" /> : <ChevronDown size={14} className="print:hidden" />}
                        Grupo / categoría
                      </button>
                    </th>
                    <th className="px-3 py-1.5 text-right">Salidas</th>
                    <th className="px-3 py-1.5 text-right">Monto {verEn}</th>
                    <th className="px-3 py-1.5 text-right w-40">% del total</th>
                  </tr>
                </thead>
                <tbody>
                  {grupos.map((g) => {
                    const abierto = !colapsados.has(g.id);
                    const pg = pctTotal(g[clave]);
                    const cats = [...g.categorias].sort((a, b) => b[clave] - a[clave]);
                    return (
                      <React.Fragment key={g.id}>
                        <tr
                          {...filaClickeable(() => alternar(g.id))}
                          className={`border-t bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer ${FOCO_FILA}`}
                        >
                          <td className="px-3 py-1.5 font-semibold text-slate-800">
                            <span className="inline-flex items-center gap-1">
                              {abierto ? <ChevronDown size={14} className="text-slate-400 print:hidden" /> : <ChevronRight size={14} className="text-slate-400 print:hidden" />}
                              {g.nombre}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{g.cantidad}</td>
                          <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap">{pref(verEn)}{formatMonto(g[clave])}</td>
                          <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2"><BarraPct valor={pg} clase="bg-red-500" />{formatPct(pg)}</div>
                          </td>
                        </tr>
                        {abierto && cats.map((c) => {
                          const pc = pctTotal(c[clave]);
                          return (
                            <tr
                              key={`${g.id}-${c.id ?? 'sin'}`}
                              {...filaClickeable(() => setDetalle({ linea: c, grupoNombre: g.nombre }))}
                              title="Ver movimientos"
                              className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${FOCO_FILA}`}
                            >
                              <td className="px-3 py-1.5 text-slate-700" style={{ paddingLeft: 12 + 20 }}>
                                {c.nombre}
                              </td>
                              <td className="px-3 py-1.5 text-right text-slate-500">{c.cantidad}</td>
                              <td className="px-3 py-1.5 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(c[clave])}</td>
                              <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-2"><BarraPct valor={pc} clase="bg-red-300" />{formatPct(pc)}</div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {grupos.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-6 text-center text-slate-400">
                        No hay egresos registrados en {nombreMes(mes)}.
                      </td>
                    </tr>
                  )}
                </tbody>
                {grupos.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 font-bold text-slate-800">
                      <td className="px-3 py-1.5">Total</td>
                      <td className="px-3 py-1.5 text-right">{datos.cantidad}</td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(total)}</td>
                      <td className="px-3 py-1.5 text-right">{formatPct(total > 0 ? 1 : 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {datos.fuera_del_total.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-3 py-1.5">Fuera del 100% · no es gasto</th>
                      <th className="px-3 py-1.5 text-right">Salidas</th>
                      <th className="px-3 py-1.5 text-right">Monto {verEn}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.fuera_del_total.map((g) => (
                      <React.Fragment key={g.id}>
                        <tr className="border-t bg-slate-50">
                          <td className="px-3 py-1.5 font-semibold text-slate-600">{g.nombre}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{g.cantidad}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-slate-600 whitespace-nowrap">{pref(verEn)}{formatMonto(g[clave])}</td>
                        </tr>
                        {g.categorias.map((c) => (
                          <tr
                            key={`${g.id}-${c.id}`}
                            {...filaClickeable(() => setDetalle({ linea: c, grupoNombre: g.nombre }))}
                            title="Ver movimientos"
                            className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${FOCO_FILA}`}
                          >
                            <td className="px-3 py-1.5 text-slate-500" style={{ paddingLeft: 12 + 20 }}>{c.nombre}</td>
                            <td className="px-3 py-1.5 text-right text-slate-400">{c.cantidad}</td>
                            <td className="px-3 py-1.5 text-right text-slate-500 whitespace-nowrap">{pref(verEn)}{formatMonto(c[clave])}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400 print:hidden">
            Tocá una categoría para ver sus movimientos. No incluye transferencias entre cuentas propias ni las categorías marcadas "No es gasto" en Categorías. Los egresos en Bs se convierten con la tasa del día de cada
            movimiento (en USD, la tasa manual del movimiento si la tiene).
          </p>
        </>
      )}
    </div>
  );
}
